import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  FileBarChart,
  Loader2,
  AlertTriangle,
  Sparkles,
  Bot,
  CalendarClock,
  ListChecks,
  Ban,
  History,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useGlobalToast } from "@/contexts/ToastContext";

export interface ReportsPlatformConfig {
  /** Base de la API del asistente, p. ej. "/admin/meta-ads/ai". */
  apiBase: string;
  description: string;
}

/**
 * Informes de campañas con IA (superadmin, Google Ads y Meta Ads). El backend reúne los datos de
 * Google Ads + registros reales y genera el informe en segundo plano; aquí se
 * consulta el estado cada pocos segundos mientras se genera.
 */

interface ReportContent {
  resumen: string;
  estado_general: "bien" | "mejorable" | "preocupante" | "sin_datos_suficientes";
  kpis: Array<{ nombre: string; valor: string; variacion: string }>;
  hallazgos: Array<{ area: string; gravedad: "alta" | "media" | "baja"; titulo: string; detalle: string }>;
  recomendaciones: Array<{
    prioridad: "alta" | "media" | "baja";
    titulo: string;
    accion: string;
    motivo: string;
    impacto_esperado: string;
    cuando: "ahora" | "esta_semana" | "esperar_aprendizaje" | "proximo_informe";
    campana: string;
    peticion_asistente: string;
  }>;
  negativas_sugeridas: Array<{
    texto: string;
    concordancia: "EXACT" | "PHRASE" | "BROAD";
    campaignId: string;
    campana: string;
    motivo: string;
  }>;
  seguimiento_informe_anterior: string;
  limitaciones_datos: string[];
  proxima_revision: string;
}

interface Report {
  id: string;
  days: number;
  model: string;
  status: "generating" | "ready" | "failed";
  errorMessage: string | null;
  content: ReportContent | null;
  inputTokens: number;
  outputTokens: number;
  createdAt: string;
}

const RANGES = [7, 14, 30, 90] as const;

const ESTADO: Record<ReportContent["estado_general"], { label: string; className: string }> = {
  bien: { label: "Bien", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  mejorable: { label: "Mejorable", className: "bg-amber-50 text-amber-700 border-amber-200" },
  preocupante: { label: "Preocupante", className: "bg-red-50 text-red-700 border-red-200" },
  sin_datos_suficientes: { label: "Sin datos suficientes", className: "bg-gray-50 text-gray-600 border-gray-200" },
};

const PRIORIDAD: Record<string, string> = {
  alta: "bg-red-50 text-red-700 border-red-200",
  media: "bg-amber-50 text-amber-700 border-amber-200",
  baja: "bg-gray-50 text-gray-600 border-gray-200",
};

const CUANDO: Record<string, string> = {
  ahora: "Ahora",
  esta_semana: "Esta semana",
  esperar_aprendizaje: "Tras el aprendizaje",
  proximo_informe: "Próximo informe",
};

const AREA: Record<string, string> = {
  estructura: "Estructura",
  presupuesto: "Presupuesto",
  palabras_clave: "Palabras clave",
  terminos_busqueda: "Términos de búsqueda",
  anuncios: "Anuncios",
  segmentacion: "Segmentación",
  embudo: "Embudo",
  audiencias: "Audiencias",
  creatividades: "Creatividades",
  ubicaciones: "Ubicaciones",
  frecuencia: "Frecuencia",
  medicion: "Medición",
};

const MATCH: Record<string, string> = { EXACT: "exacta", PHRASE: "frase", BROAD: "amplia" };

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Badge({ className, children }: { className: string; children: string }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${className}`}>
      {children}
    </span>
  );
}

function Section({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
        {icon} {title}
      </h3>
      {children}
    </section>
  );
}

function ReportView({ report, onAskAssistant }: { report: Report; onAskAssistant: (text: string) => void }) {
  const content = report.content;
  if (!content) return null;
  const estado = ESTADO[content.estado_general];

  const negativesRequest = `Prepara estas palabras clave negativas sugeridas en el informe (agrupadas por campaña):\n${content.negativas_sugeridas
    .map((n) => `- "${n.texto}" (${n.concordancia}) en la campaña ${n.campaignId}`)
    .join("\n")}`;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <Badge className={estado.className}>{estado.label}</Badge>
          <span className="text-xs text-gray-500">
            {formatDate(report.createdAt)} · últimos {report.days} días · {report.model}
          </span>
        </div>
        <p className="text-sm text-gray-800 leading-relaxed">{content.resumen}</p>
        {content.kpis.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {content.kpis.map((kpi) => (
              <div key={kpi.nombre} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                <p className="text-xs text-gray-500">{kpi.nombre}</p>
                <p className="text-base font-bold text-gray-900 mt-0.5">{kpi.valor}</p>
                <p className="text-xs text-gray-500 mt-0.5">{kpi.variacion}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <Section title="Recomendaciones" icon={<ListChecks className="w-5 h-5 text-primary-600" />}>
        <div className="space-y-3">
          {content.recomendaciones.map((rec, index) => (
            <div key={index} className="rounded-lg border border-gray-100 p-3">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <p className="font-medium text-gray-900 text-sm">{rec.titulo}</p>
                <div className="flex gap-1.5">
                  <Badge className={PRIORIDAD[rec.prioridad] ?? PRIORIDAD.baja}>{`Prioridad ${rec.prioridad}`}</Badge>
                  <Badge className="bg-primary-50 text-primary-700 border-primary-200">{CUANDO[rec.cuando] ?? rec.cuando}</Badge>
                </div>
              </div>
              <p className="text-sm text-gray-700 mt-2">{rec.accion}</p>
              <p className="text-xs text-gray-500 mt-2">
                <strong>Por qué:</strong> {rec.motivo}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                <strong>Impacto esperado:</strong> {rec.impacto_esperado}
              </p>
              {rec.campana && <p className="text-xs text-gray-400 mt-1">{rec.campana}</p>}
              {rec.peticion_asistente && (
                <button
                  onClick={() => onAskAssistant(rec.peticion_asistente)}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-primary-200 text-primary-700 rounded-lg hover:bg-primary-50"
                >
                  <Bot className="w-3.5 h-3.5" /> Preparar con el asistente
                </button>
              )}
            </div>
          ))}
        </div>
      </Section>

      {content.negativas_sugeridas.length > 0 && (
        <Section title="Negativas sugeridas" icon={<Ban className="w-5 h-5 text-primary-600" />}>
          <div className="divide-y divide-gray-100">
            {content.negativas_sugeridas.map((neg, index) => (
              <div key={index} className="py-2 text-sm">
                <p className="text-gray-900">
                  <strong>"{neg.texto}"</strong>{" "}
                  <span className="text-xs text-gray-500">
                    ({MATCH[neg.concordancia] ?? neg.concordancia}) · {neg.campana}
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{neg.motivo}</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => onAskAssistant(negativesRequest)}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-primary-200 text-primary-700 rounded-lg hover:bg-primary-50"
          >
            <Bot className="w-3.5 h-3.5" /> Preparar todas con el asistente
          </button>
        </Section>
      )}

      <Section title="Hallazgos" icon={<Sparkles className="w-5 h-5 text-primary-600" />}>
        <div className="space-y-3">
          {content.hallazgos.map((h, index) => (
            <div key={index} className="text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={PRIORIDAD[h.gravedad] ?? PRIORIDAD.baja}>{AREA[h.area] ?? h.area}</Badge>
                <p className="font-medium text-gray-900">{h.titulo}</p>
              </div>
              <p className="text-gray-600 mt-1">{h.detalle}</p>
              <button
                onClick={() =>
                  onAskAssistant(
                    `Hallazgo del informe del ${formatDate(report.createdAt)} (${AREA[h.area] ?? h.area}, gravedad ${h.gravedad}): "${h.titulo}". ${h.detalle}\n\nRevísalo con los datos actuales de la cuenta y prepara los cambios concretos para solucionarlo. Si no conviene tocar nada todavía, explícame por qué.`,
                  )
                }
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-primary-200 text-primary-700 rounded-lg hover:bg-primary-50"
              >
                <Bot className="w-3.5 h-3.5" /> Resolver con el asistente
              </button>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Seguimiento y límites" icon={<CalendarClock className="w-5 h-5 text-primary-600" />}>
        <div className="space-y-3 text-sm text-gray-700">
          <p>
            <strong>Respecto al informe anterior:</strong> {content.seguimiento_informe_anterior}
          </p>
          <p>
            <strong>Próxima revisión:</strong> {content.proxima_revision}
          </p>
          {content.limitaciones_datos.length > 0 && (
            <div>
              <p className="font-medium">Limitaciones de los datos</p>
              <ul className="list-disc pl-5 mt-1 space-y-1 text-gray-600">
                {content.limitaciones_datos.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-xs text-gray-400">
            Tokens: {report.inputTokens.toLocaleString("es-ES")} de entrada · {report.outputTokens.toLocaleString("es-ES")} de salida
          </p>
        </div>
      </Section>
    </div>
  );
}

export default function AdsReports({
  config,
  onAskAssistant,
}: {
  config: ReportsPlatformConfig;
  onAskAssistant: (text: string) => void;
}) {
  const { showToast } = useGlobalToast();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [days, setDays] = useState<(typeof RANGES)[number]>(30);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await apiClient.get<{ success: true; data: Report[] }>(`${config.apiBase}/reports`);
      setReports(data.data);
      setError(null);
      setSelectedId((current) => current ?? data.data.find((r) => r.status === "ready")?.id ?? null);
    } catch (err) {
      if (!silent) setError(extractApiErrorMessage(err, "No se pudieron cargar los informes"));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [config.apiBase]);

  useEffect(() => {
    void load();
  }, [load]);

  const generating = reports.find((r) => r.status === "generating") ?? null;

  useEffect(() => {
    if (!generating) return;
    const timer = window.setInterval(() => {
      void load(true);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [generating, load]);

  // Al terminar el informe en curso, se abre solo.
  const [watchingId, setWatchingId] = useState<string | null>(null);
  useEffect(() => {
    if (!watchingId) return;
    const report = reports.find((r) => r.id === watchingId);
    if (report && report.status !== "generating") {
      setWatchingId(null);
      if (report.status === "ready") {
        setSelectedId(report.id);
        showToast("Informe listo", "success");
      } else {
        showToast(report.errorMessage ?? "El informe ha fallado", "error");
      }
    }
  }, [reports, watchingId, showToast]);

  const handleGenerate = useCallback(async () => {
    setStarting(true);
    try {
      const { data } = await apiClient.post<{ success: true; data: Report }>(`${config.apiBase}/reports`, { days });
      setReports((prev) => [data.data, ...prev]);
      setWatchingId(data.data.id);
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo iniciar el informe"), "error");
      void load(true);
    } finally {
      setStarting(false);
    }
  }, [days, load, showToast, config.apiBase]);

  const selected = reports.find((r) => r.id === selectedId) ?? null;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileBarChart className="w-5 h-5 text-primary-600" /> Informe de campañas con IA
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {config.description}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {RANGES.map((r) => (
                <button
                  key={r}
                  onClick={() => setDays(r)}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    days === r ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {r} días
                </button>
              ))}
            </div>
            <button
              onClick={() => void handleGenerate()}
              disabled={starting || !!generating}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {starting || generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {generating ? "Generando…" : "Generar informe"}
            </button>
          </div>
        </div>
        {generating && (
          <p className="mt-3 text-sm text-primary-700 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Analizando la cuenta (iniciado {formatDate(generating.createdAt)})…
          </p>
        )}
      </div>

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando informes…
        </p>
      ) : error ? (
        <p className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle className="w-4 h-4" /> {error}
        </p>
      ) : (
        <div className="grid lg:grid-cols-[1fr_260px] gap-4 items-start">
          <div className="min-w-0">
            {selected?.status === "ready" ? (
              <ReportView report={selected} onAskAssistant={onAskAssistant} />
            ) : selected?.status === "failed" ? (
              <p className="bg-white rounded-xl border border-gray-200 p-5 text-sm text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {selected.errorMessage ?? "El informe falló"}
              </p>
            ) : (
              <p className="bg-white rounded-xl border border-gray-200 p-6 text-sm text-gray-500 text-center">
                {reports.length === 0 ? "Todavía no hay informes. Genera el primero." : "Selecciona un informe del historial."}
              </p>
            )}
          </div>

          {reports.length > 0 && (
            <aside className="bg-white rounded-xl border border-gray-200 p-3 lg:sticky lg:top-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 px-1 mb-2">
                <History className="w-3.5 h-3.5" /> Historial
              </p>
              <div className="space-y-1 max-h-[60dvh] overflow-y-auto">
                {reports.map((report) => (
                  <button
                    key={report.id}
                    onClick={() => setSelectedId(report.id)}
                    disabled={report.status === "generating"}
                    className={`w-full text-left px-2.5 py-2 rounded-lg text-sm transition-colors ${
                      report.id === selectedId ? "bg-primary-50 text-primary-800" : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <span className="block font-medium">{formatDate(report.createdAt)}</span>
                    <span className="block text-xs text-gray-500">
                      {report.days} días ·{" "}
                      {report.status === "ready"
                        ? report.content
                          ? ESTADO[report.content.estado_general].label
                          : "Listo"
                        : report.status === "generating"
                          ? "Generando…"
                          : "Fallido"}
                    </span>
                  </button>
                ))}
              </div>
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
